/**
 * The modal body for reminding an interviewer who still owes feedback.
 *
 * Split out of SchedulingModalBodies.tsx, which went over the 500-line ceiling
 * when the nudge learned to send in the recruiter's own words (#328). The seam
 * is the one that was already there: the other two bodies are about SLOTS —
 * blocking them, asking the panel to move a meeting — and this one is about a
 * person who owes us something.
 */
import { useEffect, useState } from 'react';
import { ModalHeader, ModalTitle } from '@finn-auto/design-system/atoms/modal';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Textarea } from '@finn-auto/design-system/atoms/textarea';
import { showToast } from '@finn-auto/design-system/atoms/toast';
import * as api from '../../services/taActionBoardApi';
import type { NudgeSender } from '../../services/taActionBoardApi';
import { startSlackConnect } from '../../services/taSlackConnectApi';
import { MAX_NUDGE_CHARS } from './nudgeLimits';
import type { RoleBoard, SlaActNowItem } from '../../types/taActionBoard';
import { ModalActions } from './ModalActions';
import { fullNameFromEmail } from './format';
import { LanguageToggle, type DraftLanguage } from './LanguageToggle';

/**
 * "💬 Nudge for feedback" — the interview happened, the scorecard is missing.
 *
 * Two things the recruiter controls, and one they are told:
 *
 *  · **The words.** The proxy drafts them; the textarea is the recruiter's. A fixed reminder read
 *    the same whether the interview was yesterday or a fortnight ago, and whether or not the
 *    interviewer had already been chased in person (Theresa, Sep 2026).
 *  · **Nothing until confirm**, as before.
 *  · **Who it comes from.** Resolved by the server before this renders, because "it will come
 *    from you" is a promise worth being sure of. Not connected, or the flow not configured on
 *    this proxy ⇒ the TA bot sends it, exactly as it used to, and the footer says so with the way
 *    to change it.
 */
export function NudgeBody({
  role,
  item,
  onConfirm,
  onCancel,
}: {
  role: RoleBoard;
  item: SlaActNowItem;
  onConfirm: (text: string, language?: DraftLanguage) => void;
  onCancel: () => void;
}) {
  const [state, setState] = useState<
    | { phase: 'loading' }
    | {
        phase: 'ready';
        recipients: { email: string; name: string | null }[];
        stageName: string | null;
        sender: NudgeSender;
        canConnect: boolean;
      }
    | { phase: 'error'; message: string }
  >({ phase: 'loading' });
  const [text, setText] = useState('');
  const [connecting, setConnecting] = useState(false);
  /**
   * Which language the interviewer reads.
   *
   * TWO values, the same split the panel ask uses. `chosen` is null until the
   * recruiter picks one and is the ONLY fetch dependency — seeding the shown
   * value from the first draft would change a dependency and look the same ask
   * up twice on every open. `rendered` is what the draft in the box is actually
   * written in, as the proxy reports it.
   */
  const [chosen, setChosen] = useState<DraftLanguage | null>(null);
  const [rendered, setRendered] = useState<DraftLanguage | null>(null);
  /** The recruiter has typed. Switching re-drafts, so it is confirmed first. */
  const [touched, setTouched] = useState(false);
  const language = chosen ?? rendered;
  /**
   * The draft in the box is in the language the recruiter just switched AWAY
   * from. DERIVED rather than a `setRedrafting(true)` in the effect, which
   * lints as a cascading render — and which the panel ask avoids for the same
   * reason. Confirming here would send the previous language's text.
   */
  const redrafting = chosen !== null && rendered !== chosen;

  useEffect(() => {
    let cancelled = false;
    api
      .fetchNudgeRecipients(item.opportunityId, role.roleTitle, chosen ?? undefined)
      .then((r) => {
        if (cancelled) return;
        setState({
          phase: 'ready',
          recipients: r.recipients,
          stageName: r.stageName,
          sender: r.sender || 'bot',
          // Older proxies do not send it. Absent ⇒ assume connecting works, which is the state
          // this field exists to correct, not to create.
          canConnect: r.canConnect !== false,
        });
        // The server's draft seeds the box. Sending it untouched is therefore byte-identical to
        // what the old fixed-text nudge produced.
        setText(r.text || '');
        setTouched(false);
        /*
          Display and staleness only — never a fetch dependency, or this re-runs.

          ⚠️ `r.language` ONLY. It used to fall back to `chosen`, which labelled
          an old proxy's tag-derived draft as the one that was asked for: the
          staleness check then went quiet and the recruiter could send text in
          the language they had just switched away from (CodeRabbit, PR #576).
          Unreported now means unswitched, which keeps confirm shut and says so
          below — a locked toggle during a rollout beats a wrong-language DM.
        */
        setRendered(r.language ?? null);
      })
      .catch(() => {
        if (cancelled) return;
        setState({ phase: 'error', message: 'Could not look up who owes this feedback.' });
      });
    return () => {
      cancelled = true;
    };
  }, [item.opportunityId, role.roleTitle, chosen]);

  /** Full-page redirect, like the Gmail grant: a popup here is blocked as often as not. */
  const connectSlack = async () => {
    setConnecting(true);
    try {
      const { authorizeUrl } = await startSlackConnect(`${window.location.pathname}${window.location.search}`);
      window.location.assign(authorizeUrl);
    } catch {
      setConnecting(false);
      showToast({ status: 'error', description: 'Could not open Slack — try again in a moment.' });
    }
  };

  const recipients = state.phase === 'ready' ? state.recipients : [];
  const sender = state.phase === 'ready' ? state.sender : 'bot';
  const canConnect = state.phase === 'ready' ? state.canConnect : false;
  return (
    <>
      <ModalHeader>
        <ModalTitle>Remind the interviewer about {item.name}?</ModalTitle>
      </ModalHeader>
      <div className="space-y-3">
        {state.phase === 'loading' && (
          <p className="text-sm text-gray-500">Looking up who ran the interview…</p>
        )}
        {state.phase === 'error' && <p className="text-sm text-[#791F1F]">{state.message}</p>}
        {state.phase === 'ready' && (
          <>
            <p className="text-sm text-gray-600">
              {recipients.length > 0
                ? `A Slack DM goes to ${recipients.length === 1 ? 'this interviewer' : 'these interviewers'}, asking them to submit their ${state.stageName || 'interview'} feedback for ${item.name}.`
                : 'Lever lists no interviewer for this stage, so there is nobody to remind. Check the panel in Lever.'}
            </p>
            {recipients.length > 0 && (
              <>
                <ul className="space-y-1 rounded-lg bg-gray-50 p-3">
                  {recipients.map((r) => (
                    <li key={r.email} className="text-sm text-gray-700">
                      {r.name || fullNameFromEmail(r.email)}{' '}
                      <span className="text-gray-400">{r.email}</span>
                    </li>
                  ))}
                </ul>
                <div>
                  <label htmlFor="nudge-text" className="text-xs font-semibold text-gray-800">
                    Message
                  </label>
                  <p className="mb-1 text-xs text-gray-500">
                    Edit it before sending — the same message goes to everyone listed above.
                  </p>
                  <Textarea
                    id="nudge-text"
                    value={text}
                    // Locked while the switched-to draft is in flight: the
                    // response REPLACES this box, so anything typed meanwhile
                    // was silently discarded (CodeRabbit, PR #576).
                    disabled={redrafting}
                    onChange={(e) => {
                      setTouched(true);
                      setText(e.target.value);
                    }}
                    rows={7}
                    maxLength={MAX_NUDGE_CHARS}
                  />
                  {redrafting && rendered !== null && (
                    <p className="mt-1 text-xs text-gray-500">Re-reading it in {chosen?.toUpperCase()}…</p>
                  )}
                  {redrafting && rendered === null && (
                    <p className="mt-1 text-xs text-[#791F1F]">
                      This version of the server could not switch the language — the draft above is
                      unchanged. Reload the page and try again.
                    </p>
                  )}
                  {/*
                    The interviewer's language, not the candidate's. Re-reads the
                    whole draft, so it is confirmed when the recruiter has typed —
                    switching would otherwise discard their rewrite silently.
                  */}
                  {language && (
                    <div className="mt-2">
                      <LanguageToggle
                        value={language}
                        onChange={setChosen}
                        hasEdits={touched}
                        disabled={redrafting}
                        label="They read"
                      />
                    </div>
                  )}
                </div>
                <SenderNote
                  sender={sender}
                  canConnect={canConnect}
                  connecting={connecting}
                  onConnect={connectSlack}
                />
              </>
            )}
          </>
        )}
      </div>
      <ModalActions
        confirmLabel="Send reminder"
        /*
          `redrafting` is in here because the box keeps the PREVIOUS language's
          text while the new one is fetched — confirming mid-switch would send
          the language the recruiter just switched away from.
        */
        confirmDisabled={
          state.phase !== 'ready' || recipients.length === 0 || !text.trim() || redrafting
        }
        onConfirm={() => onConfirm(text, language ?? undefined)}
        onCancel={onCancel}
      />
    </>
  );
}

/**
 * Who the DM will come from, said plainly before it is sent.
 *
 * The whole point of the personal sender is that the interviewer sees a colleague rather than a
 * bot — so a recruiter who assumes it is personal and is wrong would be misled by silence. The
 * bot case is not an error: it is how this worked until now, and it still sends.
 */
function SenderNote({
  sender,
  canConnect,
  connecting,
  onConnect,
}: {
  sender: NudgeSender;
  /** Is the per-recruiter Slack flow configured on this proxy at all? */
  canConnect: boolean;
  connecting: boolean;
  onConnect: () => void;
}) {
  if (sender === 'me') {
    return (
      <p className="rounded-md bg-[#DCF5E4] px-3 py-2 text-xs text-[#136C34]">
        Sent from <strong>your own Slack</strong> — it lands in your DM with them, and their reply
        comes back to you.
      </p>
    );
  }
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-[#FCEEE2] px-3 py-2">
      <p className="text-xs text-[#854F0B]">
        Sent by the <strong>TA bot</strong> on your behalf.
        {canConnect
          ? ' Connect your Slack to send it yourself — a message from a colleague carries more weight, and replies reach you instead of a bot.'
          : ' Sending these from your own Slack is not switched on for this workspace yet.'}
      </p>
      {/*
        Only offered where connecting can actually succeed. The flow needs a redirect URL and the
        chat:write USER scope on the Slack app; until those exist the connect route 503s, and a
        button answering "try again in a moment" describes a setup step as a transient glitch.
      */}
      {canConnect && (
        <Button size="sm" variant="outline" onClick={onConnect} loading={connecting}>
          Connect Slack
        </Button>
      )}
    </div>
  );
}
